import React, { useState } from "react";
import { ChevronDown, Calendar } from "lucide-react";
import { TrendUp, ExportCurve, Add } from "iconsax-reactjs";
const Customers = () => {
  const [selectedFilters, setSelectedFilters] = useState({
    all: "All customers",
    services: "Services",
    value: "Value",
    assigned: "Assigned to",
  });

  const StatCard = ({ title, value, change }) => (
    <div className="bg-[#0C0E12] rounded-xl p-6 border border-gray-700/50 hover:border-gray-600/50 transition-colors">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[#94979C] text-[14px] font-medium">{title}</div>
        <div className="flex items-center justify-between">
          <button className="text-gray-400 hover:text-gray-300 p-1">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <circle cx="8" cy="3" r="1.5" />
              <circle cx="8" cy="8" r="1.5" />
              <circle cx="8" cy="13" r="1.5" />
            </svg>
          </button>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-end justify-between">
          <div className="text-[#F7F7F7] text-[28px] font-bold">{value}</div>
          <div className="flex items-center text-white text-sm font-medium">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className="mr-1"
            >
              <path
                d="M8 12V4M8 4L4 8M8 4L12 8"
                stroke="#4ADE80"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {change}%
          </div>
        </div>
      </div>
    </div>
  );

  const CustomerRow = ({
    company,
    healthScore,
    services,
    value,
    lastActivity,
  }) => (
    <tr className="border-b border-gray-800 hover:bg-gray-800/30 transition-colors">
      <td className="py-4 px-4 text-white font-medium">{company}</td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <div className="w-32 bg-gray-800 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full"
              style={{ width: `${healthScore}%` }}
            ></div>
          </div>
          <span className="text-white text-sm font-medium">{healthScore}%</span>
        </div>
      </td>
      <td className="py-4 px-4">
        <div className="flex flex-wrap gap-1.5">
          {services.map((service, idx) => (
            <span
              key={idx}
              className="inline-flex items-center text-gray-400 text-xs"
            >
              <span className="mr-1">•</span> {service}
            </span>
          ))}
        </div>
      </td>
      <td className="py-4 px-4 text-white font-medium">{value}</td>
      <td className="py-4 px-4 text-gray-400">{lastActivity}</td>
      <td className="py-4 px-4 text-right">
        <button className="text-gray-400 hover:text-white transition-colors p-1">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="8" cy="3" r="1.5" />
            <circle cx="8" cy="8" r="1.5" />
            <circle cx="8" cy="13" r="1.5" />
          </svg>
        </button>
      </td>
    </tr>
  );

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white font-sans">
      <div className="p-4 lg:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Title Section */}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 text-white">
              Customers
            </h1>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
              Manage all your audio files, summaries and AI insights.
            </p>
          </div>

          {/* Buttons Section */}
          <div className="flex flex-col md:flex-row gap-2 sm:gap-3 shrink-0">
            {/* Export Button */}
            <button className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors text-sm sm:text-base font-medium min-w-0">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M14 10v2.667A1.333 1.333 0 0112.667 14H3.333A1.333 1.333 0 012 12.667V10M11.333 5.333L8 2m0 0L4.667 5.333M8 2v8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="whitespace-nowrap">Export</span>
            </button>

            {/* Import Button */}
            <button className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors text-sm sm:text-base font-medium min-w-0">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M14 10v2.667A1.333 1.333 0 0112.667 14H3.333A1.333 1.333 0 012 12.667V10M4.667 6.667L8 10m0 0l3.333-3.333M8 10V2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="whitespace-nowrap">Import</span>
            </button>

            {/* Bulk Actions Button */}
            <button className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors text-sm sm:text-base font-medium min-w-0">
              <span className="whitespace-nowrap">Bulk Actions</span>
            </button>

            {/* Add Customer Button */}
            <button
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                border: "1px solid",
                borderImageSource:
                  "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: `
      0px 36px 32px -16px #427BCA36,
      0px 8px 16px 0px #2F578F1F,
      0px 2px 4px -8px #427BCA14,
      0px 8px 24px -16px #FFFFFF3D inset,
      0px -24px 32px 0px #FFFFFF38 inset,
      0px 0px 12px 0px #FFFFFF inset
    `,
                borderImageSlice: "1",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  "radial-gradient(50% 50% at 50% 50%, #5A9BE8 0%, #427BCA 100%)";
                e.currentTarget.style.boxShadow = `
        0px 42px 36px -12px #427BCA80,
        0px 12px 20px 0px #2F578F3D,
        0px 4px 8px -4px #427BCA28,
        0px 12px 28px -12px #427BCA inset,
        0px -28px 36px 0px #FFFFFF4D inset,
        0px 0px 16px 0px #FFFFFF inset
      `;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background =
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)";
                e.currentTarget.style.boxShadow = `
      0px 36px 32px -16px #427BCA36,
      0px 8px 16px 0px #2F578F1F,
      0px 2px 4px -8px #427BCA14,
      0px 8px 24px -16px #FFFFFF3D inset,
      0px -24px 32px 0px #FFFFFF38 inset,
      0px 0px 12px 0px #FFFFFF inset
    `;
              }}
            >
              <Add size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Add Customer</span>
            </button>
          </div>
        </div>

        {/* Header with filters */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {Object.entries(selectedFilters).map(([key, value]) => (
              <button
                key={key}
                className="flex items-center gap-2 px-3 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-sm text-gray-300 hover:bg-gray-800 transition-colors"
              >
                {value}
                <ChevronDown size={14} />
              </button>
            ))}
          </div>

          <button className="flex items-center gap-2 px-3 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-sm text-gray-300 hover:bg-gray-800 transition-colors self-start lg:self-auto">
            <Calendar size={14} />
            Select dates
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <StatCard title="Active Customers" value="1,248" change="70" />
          <StatCard title="Service Health" value="92" change="70" />
          <StatCard title="Revenue Flow" value="£184.5k" change="70" />
        </div>

        {/* Table */}
        <div className="bg-[#0C0E12] rounded-lg border border-gray-700 mb-8">
          {/* Table Header Section */}
          <div className="flex items-center justify-between p-6 border-b border-gray-700">
            <div>
              <h2 className="text-xl font-semibold text-white">
                Customers
                <span className="text-gray-400 text-sm ml-2 font-normal">
                  Keep track of your customers.
                </span>
              </h2>
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-sm text-gray-300 hover:bg-gray-800 transition-colors">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M14 10v2.667A1.333 1.333 0 0112.667 14H3.333A1.333 1.333 0 012 12.667V10M11.333 5.333L8 2m0 0L4.667 5.333M8 2v8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Export
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-[#0C0E12]">
                <tr className="border-b border-gray-800">
                  <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                    Company
                  </th>
                  <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                    Health Score
                  </th>
                  <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                    Services
                  </th>
                  <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                    Value
                  </th>
                  <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                    Last Activity
                  </th>
                  <th className="text-right py-4 px-4 text-gray-400 font-medium text-sm"></th>
                </tr>
              </thead>
              <tbody>
                <CustomerRow
                  company="Beta Solutions"
                  healthScore={90}
                  services={["Cloud Storage", "High-Speed Internet"]}
                  value="£55,000"
                  lastActivity="1 day ago"
                />
                <CustomerRow
                  company="Gamma Innovations"
                  healthScore={70}
                  services={["Cybersecurity", "Network Solutions"]}
                  value="£60,000"
                  lastActivity="3 days ago"
                />
                <CustomerRow
                  company="Delta Enterprises"
                  healthScore={80}
                  services={["Video Conferencing", "Fiber Optic"]}
                  value="£85,000"
                  lastActivity="1 week ago"
                />
                <CustomerRow
                  company="Epsilon Technologies"
                  healthScore={80}
                  services={["Data Analytics", "Wireless Communication"]}
                  value="£70,000"
                  lastActivity="2 weeks ago"
                />
                <CustomerRow
                  company="Zeta Industries"
                  healthScore={80}
                  services={["IoT Solutions", "Satellite Internet"]}
                  value="£75,000"
                  lastActivity="1 month ago"
                />
              </tbody>
            </table>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 border-t border-gray-700 gap-4">
            <div className="flex gap-2">
              <button className="px-4 py-2 bg-[#0C0E12] border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors text-sm">
                Previous
              </button>
              <button className="px-4 py-2 bg-[#0C0E12] border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors text-sm">
                Next
              </button>
            </div>
            <span className="text-gray-400 text-sm">Page 1 of 10</span>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Quick Actions */}
          <div className="bg-[#0C0E12] rounded-lg border border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-white mb-6">
              Quick Actions
            </h2>

            <div className="space-y-4">
              {/* Action 1 */}
              <div className="p-4 rounded-lg hover:bg-gray-800/50 transition-colors border border-gray-800">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-white text-sm font-medium">
                    Call Acme Corp (95% health)
                  </p>
                </div>
                <p className="text-gray-400 text-sm">Suggested time: 2-4pm</p>
              </div>

              {/* Action 2 */}
              <div className="p-4 rounded-lg hover:bg-gray-800/50 transition-colors border border-gray-800">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-white text-sm font-medium">
                    Email XYZ Inc (46% health)
                  </p>
                </div>
                <p className="text-gray-400 text-sm">Risk mitigation needed</p>
              </div>

              {/* Action 3 */}
              <div className="p-4 rounded-lg hover:bg-gray-800/50 transition-colors border border-gray-800">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-white text-sm font-medium">
                    Upsell Tech Solutions
                  </p>
                </div>
                <p className="text-gray-400 text-sm">97-day use analytics</p>
              </div>
            </div>
          </div>

          {/* AI Activity */}
          <div className="bg-[#0C0E12] rounded-lg border border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-white mb-6">
              AI Activity
            </h2>

            <div className="space-y-4">
              {/* Activity 1 */}
              <div className="p-4 rounded-lg hover:bg-gray-800/50 transition-colors border border-gray-800">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-white text-sm font-medium">
                    Acme Corp - Usage increased 25%
                  </p>
                </div>
                <p className="text-gray-400 text-sm">
                  Upsell opportunity: Data plans
                </p>
              </div>

              {/* Activity 2 */}
              <div className="p-4 rounded-lg hover:bg-gray-800/50 transition-colors border border-gray-800">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-white text-sm font-medium">
                    Biz Ltd - Support tickets +40%
                  </p>
                </div>
                <p className="text-gray-400 text-sm">
                  Health declining: Call today
                </p>
              </div>

              {/* Activity 3 */}
              <div className="p-4 rounded-lg hover:bg-gray-800/50 transition-colors border border-gray-800">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-white text-sm font-medium">
                    Global Systems - Payment early
                  </p>
                </div>
                <p className="text-gray-400 text-sm">
                  Strong customer: Send survey
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Plans Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          {/* VoIP System */}
          <div className="bg-[#0C0E12] rounded-lg border border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">VoIP System</h3>
              <span className="flex items-center gap-2 text-xs text-gray-400">
                <div className="w-2 h-2 rounded-full bg-green-400"></div>
                Active
              </span>
            </div>
            <div className="space-y-2 mb-4">
              <div className="flex items-center text-sm text-gray-400">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="mr-2"
                >
                  <path
                    d="M8 14A6 6 0 108 2a6 6 0 000 12z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                </svg>
                25 users
              </div>
              <div className="flex items-center text-sm text-gray-400">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="mr-2"
                >
                  <path
                    d="M4 6h8M4 10h8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                29th Oct 2025
              </div>
            </div>
            <div className="mb-4">
              <div className="text-2xl font-bold text-white mb-1">
                £1200{" "}
                <span className="text-sm font-normal text-gray-400">
                  per month
                </span>
              </div>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-2 mb-2">
              <div
                className="bg-blue-500 h-2 rounded-full"
                style={{ width: "92%" }}
              ></div>
            </div>
            <div className="text-right text-sm text-gray-400">92%</div>
          </div>

          {/* Fibre Broadband */}
          <div className="bg-[#0C0E12] rounded-lg border border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">Fibre Broadband</h3>
              <span className="flex items-center gap-2 text-xs text-gray-400">
                <div className="w-2 h-2 rounded-full bg-green-400"></div>
                Active
              </span>
            </div>
            <div className="space-y-2 mb-4">
              <div className="flex items-center text-sm text-gray-400">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="mr-2"
                >
                  <path
                    d="M8 14A6 6 0 108 2a6 6 0 000 12z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                </svg>
                12 users
              </div>
              <div className="flex items-center text-sm text-gray-400">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="mr-2"
                >
                  <path
                    d="M4 6h8M4 10h8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                29th Oct 2025
              </div>
            </div>
            <div className="mb-4">
              <div className="text-2xl font-bold text-white mb-1">
                £800{" "}
                <span className="text-sm font-normal text-gray-400">
                  per month
                </span>
              </div>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-2 mb-2">
              <div
                className="bg-blue-500 h-2 rounded-full"
                style={{ width: "92%" }}
              ></div>
            </div>
            <div className="text-right text-sm text-gray-400">92%</div>
          </div>

          {/* Mobile Plans */}
          <div className="bg-[#0C0E12] rounded-lg border border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">Mobile Plans</h3>
              <span className="flex items-center gap-2 text-xs text-gray-400">
                <div className="w-2 h-2 rounded-full bg-green-400"></div>
                Active
              </span>
            </div>
            <div className="space-y-2 mb-4">
              <div className="flex items-center text-sm text-gray-400">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="mr-2"
                >
                  <path
                    d="M8 14A6 6 0 108 2a6 6 0 000 12z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                </svg>
                15 users
              </div>
              <div className="flex items-center text-sm text-gray-400">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="mr-2"
                >
                  <path
                    d="M4 6h8M4 10h8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                1st Nov 2025
              </div>
            </div>
            <div className="mb-4">
              <div className="text-2xl font-bold text-white mb-1">
                £450{" "}
                <span className="text-sm font-normal text-gray-400">
                  per month
                </span>
              </div>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-2 mb-2">
              <div
                className="bg-blue-500 h-2 rounded-full"
                style={{ width: "92%" }}
              ></div>
            </div>
            <div className="text-right text-sm text-gray-400">92%</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Customers;
